export type Role = "ADMIN" | "GESTOR" | "CONSULTOR" | "LEITOR";

/** Carimbo do build, injetado pelo Vite. Ver `vite.config.ts`. */
declare const __BUILD__: string;
export const BUILD = typeof __BUILD__ === "string" ? __BUILD__ : "dev";

export type StatusVisita =
  | "AGENDADO"
  | "EM_ANDAMENTO"
  | "EM_PESQUISA"
  | "RECALL"
  | "FINANCEIRO"
  | "OPTOU_OUTRA_ESCOLA"
  | "FECHOU_MATRICULA"
  | "NAO_COMPARECEU"
  | "CANCELADO";

/** Rótulos em português — o banco guarda o enum, a tela mostra isto. */
export const ROTULO_STATUS: Record<StatusVisita, string> = {
  AGENDADO: "Agendado",
  EM_ANDAMENTO: "Em andamento",
  EM_PESQUISA: "Em pesquisa",
  RECALL: "Recall",
  FINANCEIRO: "Financeiro",
  OPTOU_OUTRA_ESCOLA: "Optou por outra escola",
  FECHOU_MATRICULA: "Fechou matrícula",
  NAO_COMPARECEU: "Não compareceu",
  CANCELADO: "Cancelado",
};

export const COR_STATUS: Record<StatusVisita, string> = {
  AGENDADO: "#1570ef",
  EM_ANDAMENTO: "#f79009",
  EM_PESQUISA: "#7a5af8",
  RECALL: "#0e8c3a",
  FINANCEIRO: "#0e8c3a",
  OPTOU_OUTRA_ESCOLA: "#d92d20",
  FECHOU_MATRICULA: "#0e8c3a",
  NAO_COMPARECEU: "#b54708",
  CANCELADO: "#667085",
};

export interface Usuario {
  id: string;
  nome: string;
  email: string;
  role: Role;
  ativo?: boolean;
  ultimoLogin?: string | null;
}

export interface Curso {
  id: string;
  nome: string;
  series?: Serie[];
}

export interface Serie {
  id: string;
  nome: string;
  cursoId?: string | null;
}

export type TipoDiferencial = "GRADE" | "INTEGRAL";

export interface Diferencial {
  id: string;
  nome: string;
  tipo: TipoDiferencial;
}

/** Turno da linha de mensalidade — decide se ela aparece no bloco 4A ou 4B. */
export type PeriodoValor = "PARCIAL" | "INTEGRAL";

export type TipoTabelaValor =
  | "MENSALIDADE"
  | "MATERIAL"
  | "BILINGUE"
  /** Piso da mensalidade parcial depois da condição exclusiva. */
  | "MINIMO"
  /** Piso da mensalidade integral depois da condição exclusiva. */
  | "MINIMO_INTEGRAL";

export interface TabelaValor {
  id: string;
  tipo: TipoTabelaValor;
  descricao: string;
  valor: string;
  ativo?: boolean;
  curso: { id: string; nome: string } | null;
  /**
   * Só em MENSALIDADE: separa o bloco 4A do 4B no formulário.
   * Nulo quer dizer "não classificada" — aí vale a leitura da descrição.
   */
  periodoValor?: PeriodoValor | null;
  /**
   * Só em MINIMO: id da linha de MENSALIDADE a que o piso se refere.
   * Piso sem referência é cadastro do formato antigo (por curso) e não vale
   * mais — o mesmo curso tem preços diferentes com e sem bilíngue.
   */
  referenciaId?: string | null;
}

export const MOTIVOS_NAO_MATRICULA: Record<string, string> = {
  VALOR: "Valor/Preço",
  DISTANCIA: "Distância",
  ESCOLA_PUBLICA: "Optou por escola pública",
  OUTRA_PARTICULAR: "Preferiu outra escola particular",
  MUDANCA_CIDADE: "Mudança de cidade",
  DESISTIU_MUDAR: "Desistiu de mudar de escola",
  FALTA_VAGA: "Falta de vaga",
  INFRAESTRUTURA: "Infraestrutura",
  METODOLOGIA: "Metodologia",
  ATENDIMENTO: "Atendimento",
  OUTRO: "Outro",
};

export interface Proposta {
  valorNormal: string | null;
  percentualDesconto: string | null;
  valorComDesconto: string | null;
  valorMatricula: string | null;
  percentualDescontoMatricula: string | null;
  valorMatriculaComDesconto: string | null;
  qtdParcelas: number | null;
  valorAnual: string | null;
  /** % do desconto que virou abatimento por pagar em dia, na data da proposta. */
  percentualAntecipado: string | null;

  valorNormalIntegral: string | null;
  percentualDescontoIntegral: string | null;
  valorComDescontoIntegral: string | null;
  valorMatriculaIntegral: string | null;
  percentualDescontoMatriculaIntegral: string | null;
  valorMatriculaComDescontoIntegral: string | null;
  qtdParcelasIntegral: number | null;
  valorAnualIntegral: string | null;

  materialCoc: string | null;
  materialBilingue: string | null;
  totalMaterial: string | null;
  qtdParcelasMaterial: number | null;
  valorParcelaMaterial: string | null;
}

/**
 * Visita é encontro marcado; orçamento é proposta sem encontro. Moram na mesma
 * tabela porque carregam exatamente os mesmos dados — o que muda é que
 * orçamento não ocupa agenda, não entra nos indicadores e não dispara os
 * WhatsApps automáticos de confirmação e lembrete.
 */
export type TipoRegistro = "VISITA" | "ORCAMENTO";

export interface Visita {
  id: string;
  tipo: TipoRegistro;
  /** Quando a proposta foi entregue pelo WhatsApp. Só orçamento usa. */
  orcamentoEnviadoEm: string | null;
  nomeResponsavel: string;
  nomeAluno: string;
  /** Número nacional, só dígitos, **sem** o código do país. */
  telefoneResponsavel: string | null;
  /** Código do país do telefone. Vazio ou nulo vale Brasil. */
  paisTelefone?: string | null;
  /** Data pura ("AAAA-MM-DD..."), sem hora — não passe por `new Date` para exibir. */
  dataNascimento: string | null;
  inicioEm: string;
  fimEm: string | null;
  local: string | null;
  indicacao: string | null;
  ondeConheceu: string | null;
  status: StatusVisita;
  motivoNaoMatricula: string | null;
  observacoes: string | null;
  curso: { id: string; nome: string } | null;
  serie: { id: string; nome: string } | null;
  periodo: PeriodoPretendido | null;
  responsavel: { id: string; nome: string } | null;
  emailResponsavel: string | null;
  /** Veio do formulário público do site. */
  origemPublica: boolean;
  /** Quando alguém da equipe abriu pela primeira vez. */
  vistaEm: string | null;
  /** Quando o registro nasceu — para visita do site, é o momento do envio. */
  criadoEm: string;
  /** Ids crus — usados para decidir se este usuário pode editar a visita. */
  responsavelId: string | null;
  criadoPorId: string | null;
  proposta: Proposta | null;
  /** Texto do campo "Observações" — corresponde ao `description` do 4.x. */
  descricao: string | null;
  nomeIrmao: string | null;
  serieIrmao: string | null;
  diferenciais: Array<{ diferencial: Diferencial }>;
  mensagens?: MensagemWhatsapp[];
}

export interface MensagemWhatsapp {
  id: string;
  tipo: "CONFIRMACAO" | "LEMBRETE" | "RECALL";
  ok: boolean;
  erro: string | null;
  tentativas: number;
  enviadoEm: string | null;
  criadoEm: string;
}

export const ROTULO_MENSAGEM: Record<MensagemWhatsapp["tipo"], string> = {
  CONFIRMACAO: "Confirmação",
  LEMBRETE: "Lembrete de 3 horas",
  RECALL: "Convite de matrícula",
};

export interface Kpis {
  total: number;
  hoje: number;
  matriculas: number;
  perdidas: number;
  emAndamento: number;
  taxaConversao: number;
  taxaPerda: number;
  receitaAnualPrevista: number;
  porStatus: Record<string, number>;
  motivosNaoEfetivacao: Array<{ motivo: string | null; quantidade: number }>;
}

export type PeriodoPretendido = "MANHA" | "TARDE" | "INTEGRAL";

/** Turno que a família quer para o aluno. */
export const ROTULO_PERIODO: Record<PeriodoPretendido, string> = {
  MANHA: "Manhã",
  TARDE: "Tarde",
  INTEGRAL: "Integral",
};

/** Visita resumida — o que as listas de pendência precisam mostrar. */
export interface VisitaResumo {
  id: string;
  nomeAluno: string;
  nomeResponsavel: string;
  telefoneResponsavel: string | null;
  paisTelefone?: string | null;
  inicioEm: string;
  status: StatusVisita;
  atualizadoEm: string;
  origemPublica: boolean;
  /** Última conversa pelo WhatsApp manual — usada no alerta de "sem movimento". */
  ultimoContatoEm: string | null;
}

export interface Pendencias {
  /** Data passou e continua como Agendado — ninguém deu baixa. */
  atrasadas: VisitaResumo[];
  /** Em andamento e sem ninguém mexer há `diasParada` dias. */
  paradas: VisitaResumo[];
  /** Em pesquisa e a família não voltou há `diasPesquisa` dias. */
  semRetorno: VisitaResumo[];
  /** Agendou pelo site e ninguém da equipe abriu ainda. */
  novasDoSite: VisitaResumo[];
  diasParada: number;
  diasPesquisa: number;
}

export interface Origens {
  origens: Array<{ origem: string; total: number; matriculas: number; taxa: number }>;
  comIndicacao: number;
  /** Visitas marcadas pela própria família na página pública. */
  pelosite: number;
  total: number;
}
